import { PrimaryDialog, SearchBox } from "@foodpilot/foods";
import { Typography, Box, Stack, Button, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useState, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ProductFormProductPackaging } from "../../../ProductForm.types";
import { PackagingListGrid } from "./PackagingListGrid";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

type PackagingsModalProps = {
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    onValidate: (newRefPackagings: ProductFormProductPackaging[]) => void;
    productPackagings: ProductFormProductPackaging[];
};

export const PackagingsModal = (props: PackagingsModalProps) => {
    const { t } = useTranslation();
    const { onValidate, isOpen, setOpen, productPackagings } = props;

    const [selectedPackagings, setSelectedPackagings] = useState(productPackagings);

    useEffect(() => {
        setSelectedPackagings(productPackagings);
    }, [productPackagings]);

    const title = (
        <Typography variant="h3" component="span">
            {t("ajouter_un_emballage")}
        </Typography>
    );

    const content = (
        <DisplayPackagings currentPackagings={selectedPackagings} setNewPackagings={setSelectedPackagings} />
    );

    return (
        <Fragment>
            <PrimaryDialog
                open={isOpen}
                setOpen={setOpen}
                options={{
                    title: title,
                    content: content,
                }}
                onValidate={() => {
                    onValidate(selectedPackagings);
                }}
                onCancel={() => {
                    setSelectedPackagings(productPackagings);
                }}
                fullWidth
                maxWidth="md"
                cancelText={t("applications.cancel")}
                validateText={t("applications.validate", "Valider")}
            />
        </Fragment>
    );
};

type DisplayPackagingsProps = {
    currentPackagings: ProductFormProductPackaging[];
    setNewPackagings: (selectedPackagings: ProductFormProductPackaging[]) => void;
};
const DisplayPackagings = (props: DisplayPackagingsProps) => {
    const { setNewPackagings, currentPackagings } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const search = useDelayedSearch();

    return (
        <Box
            component="div"
            sx={{
                borderRadius: theme.spacing(2),
                minHeight: "200px",
                maxHeight: "750px",

                overflowX: "hidden",
            }}
        >
            <Stack direction="row" justifyContent="space-between" sx={{ padding: "24px" }}>
                <SearchBox
                    variant="standalone"
                    value={search.search}
                    onChange={(value) => {
                        search.setSearch(value);
                    }}
                    placeholder={t("packagings.searching", "Rechercher un emballage")}
                />
                <Button
                    variant="white-filled-no-shadow"
                    id="filter-anchor"
                    disabled
                    startIcon={<FilterListIcon />}
                    sx={{
                        height: theme.spacing(5),
                        padding: `0 ${theme.spacing(2.5)}`,
                    }}
                >
                    {t("filtrer_la_liste")}
                </Button>
            </Stack>
            <PackagingListGrid
                delayedSearch={search.delayedSearch}
                existingPackagings={currentPackagings}
                setNewPackagings={setNewPackagings}
            />
        </Box>
    );
};
